import { AfterViewInit, Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { FreeFilterInputComponent } from '../../utils/component/free-filter-input/free-filter-input.component';
import {
  getStatusColor,
  getStatusText,
  StateFilterCBComponent
} from '../../utils/component/state-filter-combobox/state-cb.component';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Approval } from '../../core/types';
import { getMinFilterLengthText } from '../../core/util';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { messageTypes, WidgetMessageService } from '../../core/services/widget-message.service';
import { ModuleEntityService } from '../../module/services/module.entity.service';
import { LearningUnitEntityService } from '../services/learning-unit.entity.service';
import { LearningSectionEntityService } from '../../learning-section/services/learning-section.entity.service';
import { ConsumerEntityService } from '../../consumer/services/consumer.entity.service';
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, of, switchMap, tap } from 'rxjs';
import { LearningUnitEntity } from '../learning-unit-entity';
import { ModuleEntity } from '../../module/module.entity';
import { LearningSectionEntity } from '../../learning-section/learning-section-entity';
import { map } from 'rxjs/operators';
import {
  MultiComboEntitySearchComponent
} from '../../utils/component/multi-combo-entity-search/multi-combo-entity-search.component';
import { TableLoadingSpinnerComponent } from '../../utils/component/table-loading-spinner/table-loading-spinner';
import { EntityStateModel, InvalidateLoadedQueries } from '../../core/state/entity.state';
import { makeQueryKey } from '../../utils/entity-states/query-key';
import {
  FormDialogLearningUnitComponent,
  LuMetadataRow
} from '../form-dialog-learning-unit/form-dialog-learning-unit.component';
import {
  ModuleLearningUnitMetadataEntityService
} from '../../module/services/module-learning-unit-metadata.entity.service';
import { ModuleLearningUnitMetadataEntity } from '../../module/module-learning-unit-metadata.entity';
import {
  ConfirmDialogRawComponent,
  SimpleConfirmData
} from '../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { LocalStorageService } from '../../core/services/local-storage.service';

@Component({
  selector: 'pixl-learning-unit-main',
  imports: [
    MatIcon,
    MatSortModule,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    DatePipe,
    MatTooltip,
    MatIconButton,
    MatSelectModule,
    MatTooltipModule,
    MatDialogModule,
    MatListModule,
    MatInputModule,
    StateFilterCBComponent,
    FreeFilterInputComponent,
    NgClass,
    AsyncPipe,
    TranslocoModule,
    MultiComboEntitySearchComponent,
    TableLoadingSpinnerComponent,
    MatPaginator
  ],
  templateUrl: './learning-unit-main.component.html',
  styleUrl: './learning-unit-main.component.scss'
})
export class LearningUnitMainComponent implements OnInit, AfterViewInit {
  private store = inject(Store);
  private widgetService = inject(WidgetMessageService);
  private dialog = inject(MatDialog);
  private translocoService = inject(TranslocoService);

  private learningUnitService = inject(LearningUnitEntityService);
  private metadataEntityService = inject(ModuleLearningUnitMetadataEntityService);
  private moduleService = inject(ModuleEntityService);
  private learningSectionService = inject(LearningSectionEntityService);
  private consumerService = inject(ConsumerEntityService);
  private localStorageService = inject(LocalStorageService);

  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;
  learningUnits$!: Observable<LearningUnitEntity[]>;
  deletedIds$ = new BehaviorSubject<string[]>([]);

  modules$!: Observable<ModuleEntity[]>;
  learningSections$!: Observable<LearningSectionEntity[]>;
  consumers$!: Observable<ConsumerEntity[]>;

  public readonly freeFilterLen = 3;
  private freeFilter = '';
  private consumerFilter: string[] = [];
  protected moduleFilter: string[] = [];
  protected learningSectionFilter: string[] = [];
  private statusFilter = '';

  selectedModules: ModuleEntity[] = [];
  currentPageModules = 1;
  pageSizeModules = 20;
  searchQueryModules: string | null = null;

  selectedLearningSections: LearningSectionEntity[] = [];
  currentPageLearningSections = 1;
  pageSizeLearningSections = 20;
  searchQueryLearningSections: string | null = null;

  private refresh$ = new BehaviorSubject<void>(undefined);
  public currentSelectedRow = signal<string | undefined>(undefined);

  public displayedColumns: string[] = ['actions', 'id', 'title', 'subtitle', 'consumer', 'modules', 'learningSections', 'created_at', 'status'];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  pageSize = 25;
  pageIndex = 0;
  totalItems = 1000;

  private destroyRef = inject(DestroyRef);
  private pageChange$ = new BehaviorSubject<{ pageIndex: number, pageSize: number }>({ pageIndex: this.pageIndex, pageSize: this.pageSize });

  ngOnInit() {
    this.loadRelationData();

    this.learningUnits$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'include': 'modules,learningSections,consumer',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': 'title'
        };

        if (this.freeFilter?.trim()?.length >= 3) {
          query['page[number]'] = 0;
          query['filter[titleLike]'] = this.freeFilter.trim();
        }

        if (this.consumerFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[consumer][id]'] = this.consumerFilter.join(',');
        }

        if (this.moduleFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[modules][title]'] = this.moduleFilter.join(',');
        }

        if (this.learningSectionFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[learningSections][name]'] = this.learningSectionFilter.join(',');
        }

        if (this.statusFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[status]'] = this.statusFilter.trim();
        }

        return from(this.learningUnitService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.learningUnitService.allByQuery(query))),
          map((learningUnits: LearningUnitEntity[]) => learningUnits.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.learningUnitService.stateClass) as EntityStateModel<LearningUnitEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.paginator.page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery))
      )
    );

    const moduleQuery = { 'page[size]': this.pageSizeModules, 'page[number]': this.currentPageModules, 'sort': 'title' };

    this.modules$ = from(
      this.moduleService.ensureLoaded(moduleQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.moduleService.allByQuery(moduleQuery)).pipe(
          map(paginated => {
            return [...this.selectedModules, ...paginated].filter(
              (lu, index, self) => index === self.findIndex(s => s.id === lu.id)
            );
          })
        )
      )
    );

    const learningSectionQuery = { 'page[size]': this.pageSizeLearningSections, 'page[number]': this.currentPageLearningSections, 'sort': 'name' };

    this.learningSections$ = from(
      this.learningSectionService.ensureLoaded(learningSectionQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.learningSectionService.allByQuery(learningSectionQuery)).pipe(
          map(paginated => {
            return [...this.selectedLearningSections, ...paginated].filter(
              (lu, index, self) => index === self.findIndex(s => s.id === lu.id)
            );
          })
        )
      )
    );
  }

  onAdd(): void {
    const dialogRef = this.dialog.open(FormDialogLearningUnitComponent, {
      width: '1200px',
      maxWidth: 'none',
      data: {}
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<LearningUnitEntity> | undefined) => {
      if (result) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const metadataRows: LuMetadataRow[] | undefined = (result as any)._metadataRows;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (result as any)._metadataRows;

        const newEntityInstance = Object.assign(new LearningUnitEntity(), result);

        const created = await this.learningUnitService.create(newEntityInstance);

        if (metadataRows && created.id) {
          await this.saveLuMetadataRows(created.id, metadataRows);
        }

        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.showMessage('Lerneinheit erfolgreich erstellt.');
      }
    });
  }

  onEdit(learningUnit: LearningUnitEntity) {
    this.currentSelectedRow.set(learningUnit.id);

    const dialogRef = this.dialog.open(FormDialogLearningUnitComponent, {
      width: '1200px',
      maxWidth: 'none',
      data: { ...learningUnit }
    });

    dialogRef.afterClosed().subscribe(async (updatedLearningUnit: LearningUnitEntity | undefined) => {
      if (!updatedLearningUnit) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const metadataRows: LuMetadataRow[] | undefined = (updatedLearningUnit as any)._metadataRows;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (updatedLearningUnit as any)._metadataRows;

      await firstValueFrom(this.learningUnitService.update(updatedLearningUnit));

      if (metadataRows && updatedLearningUnit.id) {
        await this.syncLuMetadataRows(updatedLearningUnit.id, metadataRows);
        this.store.dispatch(new InvalidateLoadedQueries());
      }

      this.refresh$.next();

      this.showMessage('Lerneinheit erfolgreich editiert.');
    });
  }

  async onCopy(learningUnit: LearningUnitEntity) {
    this.currentSelectedRow.set(learningUnit.id);

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(learningUnit.title),
            headerText: 'Lerneinheit kopieren',
            afterNameText: 'wirklich kopieren?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    const copyEntityInstance = Object.assign(new LearningUnitEntity(), learningUnit);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (copyEntityInstance as any).id = undefined;
    copyEntityInstance.title = `${copyEntityInstance.title} (Kopie)`;
    await this.learningUnitService.create(copyEntityInstance);
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.showMessage('Lerneinheit erfolgreich dupliziert.');
  }

  async onDelete(learningUnit: LearningUnitEntity) {
    if (!learningUnit?.id) {
      console.error('ID is undefined!', learningUnit);
      return;
    }

    this.currentSelectedRow.set(learningUnit.id);

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(learningUnit.title),
            headerText: 'Lerneinheit löschen',
            afterNameText: 'wirklich löschen?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.learningUnitService.delete(learningUnit.id));

    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.showMessage('Lerneinheit erfolgreich gelöscht.');
  }

  private async saveLuMetadataRows(learningUnitId: string, rows: LuMetadataRow[]): Promise<void> {
    for (const row of rows) {
      for (const moduleId of row.moduleIds) {
        const entry = Object.assign(new ModuleLearningUnitMetadataEntity(), {
          cantonKey: row.cantonKey,
          professionKey: row.professionKey,
          module: { type: 'modules', id: moduleId },
          learningUnit: { type: 'learning-units', id: learningUnitId },
        });
        await this.metadataEntityService.create(entry);
      }
    }
  }

  private async syncLuMetadataRows(learningUnitId: string, rows: LuMetadataRow[]): Promise<void> {
    this.store.dispatch(new InvalidateLoadedQueries());

    const metadataQuery = { 'filter[learningUnit]': learningUnitId };
    await this.metadataEntityService.ensureLoaded(metadataQuery);
    const existing = this.store.selectSnapshot(
      this.metadataEntityService.allByQuery(metadataQuery)
    ) as ModuleLearningUnitMetadataEntity[];

    for (const entry of existing) {
      await firstValueFrom(this.metadataEntityService.delete(entry.id));
    }

    await this.saveLuMetadataRows(learningUnitId, rows);
  }

  private showMessage(message: string, messageTye: messageTypes = 'mtSuccess'): void {
    this.widgetService.setMessage({
      message,
      messageTye
    });
  }

  public getSearchFilterHint(): string {
    return getMinFilterLengthText(this.freeFilterLen);
  }

  public getStatusColor(approval: Approval): string {
    return getStatusColor(approval);
  }

  public getStatusText(approval: Approval): string {
    return this.translocoService.translate(getStatusText(approval));
  }

  getModuleNames(modules: { id: string }[]): string[] {
    if (!modules?.length) return [];

    const state = this.store.selectSnapshot(this.moduleService.stateClass);

    return modules.map(module => {
      const entity = this.moduleService.stateClass.byId(state)(module.id);
      return entity?.title || module.id;
    });
  }

  getConsumerName(consumerId: string): string {
    if (!consumerId) return '---';

    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer = this.consumerService.stateClass.byId(state)(consumerId);

    return consumer?.name || consumerId;
  }

  getLearningSectionNames(learningSections: { id: string }[]): string[] {
    if (!learningSections?.length) return [];

    const state = this.store.selectSnapshot(this.learningSectionService.stateClass);

    return learningSections.map(learningSection => {
      const entity = this.learningSectionService.stateClass.byId(state)(learningSection.id);
      return entity?.name || learningSection.id;
    });
  }

  public getCreatedDate(id: string): Date {
    if (!id) {
      return new Date();
    }
    return new Date(parseInt(id.substring(0, 8), 16) * 1000);
  }

  public setFreeFilter(input: string): void {
    this.currentSelectedRow.set(undefined);
    this.freeFilter = input;
    this.refresh$.next();
  }

  public setConsumerFilter(selected: string[]): void {
    this.currentSelectedRow.set(undefined);
    this.consumerFilter = selected;
    this.refresh$.next();
  }

  public setModuleFilter(selected: string[]): void {
    this.currentSelectedRow.set(undefined);
    this.moduleFilter = selected;
    this.refresh$.next();
  }

  receiveModuleData(selected: ModuleEntity[] | null) {
    this.selectedModules = selected ?? [];
    const selectedTitles = (selected ?? []).map(module => module.title);
    this.setModuleFilter(selectedTitles);
  }

  async receiveModuleQuery(query: string) {
    this.searchQueryModules = query?.trim() || null;

    if (this.searchQueryModules && this.searchQueryModules.length > 3) {
      // search mode
      const searchParams = { 'filter[titleLike]': this.searchQueryModules };
      await this.moduleService.ensureLoaded(searchParams);

      this.modules$ = this.store.select(this.moduleService.allByQuery(searchParams)).pipe(
        map(results => results)
      );
    }
    else {
      const pageQuery = { 'page[size]': this.pageSizeModules, 'page[number]': this.currentPageModules, 'sort': 'title' };
      this.modules$ = this.store.select(this.moduleService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          return [...this.selectedModules, ...paginated]
            .filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  async loadMoreModules(event: { pageNumber: number, pageSize: number }) {
    if (!this.searchQueryModules) {
      const pageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'title',
      };

      await this.moduleService.ensureLoaded(pageQuery);

      const currentModules = await firstValueFrom(this.modules$ ?? of([]));

      this.modules$ = this.store.select(this.moduleService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          return [
            ...this.selectedModules,
            ...currentModules,
            ...paginated,
          ].filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  get moduleLabels(): string {
    return this.moduleFilter?.length ?
      this.moduleFilter.join(', ') :
      'Alle Module';
  }

  public setLearningSectionFilter(selected: string[]): void {
    this.currentSelectedRow.set(undefined);
    this.learningSectionFilter = selected;
    this.refresh$.next();
  }

  receiveLearningSectionData(selected: LearningSectionEntity[] | null) {
    this.selectedLearningSections = selected ?? [];
    const selectedNames = (selected ?? []).map(lu => lu.name);
    this.setLearningSectionFilter(selectedNames);
  }

  async receiveLearningSectionQuery(query: string) {
    this.searchQueryLearningSections = query?.trim() || null;

    if (this.searchQueryLearningSections && this.searchQueryLearningSections.length > 3) {
      // search mode
      const searchParams = { 'filter[nameLike]': this.searchQueryLearningSections };
      await this.learningSectionService.ensureLoaded(searchParams);

      this.learningSections$ = this.store.select(this.learningSectionService.allByQuery(searchParams)).pipe(
        map(results => results)
      );
    }
    else {
      // default paginated mode
      const pageQuery = { 'page[size]': this.pageSizeLearningSections, 'page[number]': this.currentPageLearningSections, 'sort': 'name' };
      this.learningSections$ = this.store.select(this.learningSectionService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          return [...this.selectedModules, ...paginated]
            .filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  async loadMoreLearningSections(event: { pageNumber: number, pageSize: number }) {
    if (!this.searchQueryLearningSections) {
      const pageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'name',
      };

      await this.learningSectionService.ensureLoaded(pageQuery);

      const currentLearningSections = await firstValueFrom(this.learningSections$ ?? of([]));

      this.learningSections$ = this.store.select(this.learningSectionService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          return [
            ...this.selectedLearningSections,
            ...currentLearningSections,
            ...paginated,
          ].filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  get learningSectionLabels(): string {
    return this.learningSectionFilter?.length ?
      this.learningSectionFilter.join(', ') :
      'Alle Lernabschnitte';
  }

  public setStatusFilter(selected: string): void {
    this.currentSelectedRow.set(undefined);
    this.statusFilter = selected;
    this.refresh$.next();
  }

  trackById(index: number, module: ModuleEntity): string {
    return module.id;
  }
}
